// Member self-service page at /member: login (email + mobile the first time), forced password change,
// then package / payments / invoices / attendance / profile. Uses its own token ("memberPortal" in
// local storage), never the staff login.
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Card, CardContent, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, MenuItem, Stack, Tab, Tabs, TextField, Typography, Alert, CircularProgress, Table, TableBody,
  TableCell, TableHead, TableRow, Paper,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PrintIcon from "@mui/icons-material/Print";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Avatar, CircularProgress as Ring, Snackbar } from "@mui/material";
import { useTheme as useAppTheme } from "../../contexts/ThemeContext";
import { useReactToPrint } from "react-to-print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { formatAge } from "../../utils/age";

const STORE = "memberPortal";
const api = (path) => `${window.constants.api_url}/portal/${path}`;
const readSession = () => {
  try { return JSON.parse(localStorage.getItem(STORE)) || null; } catch { return null; }
};
const writeSession = (s) => {
  try { s ? localStorage.setItem(STORE, JSON.stringify(s)) : localStorage.removeItem(STORE); } catch { /* storage blocked */ }
};
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (d) => {
  if (!d) return "-";
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : d;
};

async function call(path, body, session, onExpired) {
  const res = await fetch(api(path), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...(session ? { Authorization: `Bearer ${session.token}` } : {}) },
    body: JSON.stringify(body || {}),
  });
  let data = {};
  try { data = await res.json(); } catch { /* not json */ }
  if (res.status === 401 && session) onExpired && onExpired();
  if (res.status === 403 && data.must_change_password) data.code = 403;
  return data;
}

const Shell = ({ colors, children, onLogout, name, plain }) => (
  <Box sx={{ minHeight: "100vh", bgcolor: "#f3f4ef" }}>
    <Box className="no-print" sx={{ background: `linear-gradient(135deg, ${colors.evergreen} 0%, ${colors.leaf} 100%)`, color: "#fff", boxShadow: "0 4px 18px rgba(0,0,0,0.18)" }}>
      <Container maxWidth="md" sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25 }}>
        <Box sx={{ bgcolor: "#fff", borderRadius: "12px", p: 0.5, display: "flex" }}>
          <Box component="img" src={`${process.env.PUBLIC_URL || ""}/images/rectangle-9@2x.png`} alt="" sx={{ height: 30 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, lineHeight: 1.1, letterSpacing: 0.2 }}>Maa Tara Tarini Gym</Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.75 }}>Member area</Typography>
        </Box>
        {name ? <Typography noWrap sx={{ opacity: 0.9, display: { xs: "none", sm: "block" }, maxWidth: 180 }}>{name}</Typography> : null}
        {onLogout ? <Button color="inherit" size="small" startIcon={<LogoutIcon />} onClick={onLogout} sx={{ borderRadius: 99, border: "1px solid rgba(255,255,255,0.4)", textTransform: "none" }}>Log out</Button> : null}
      </Container>
    </Box>
    <Container maxWidth="md" sx={{ py: plain ? 0 : 3, px: { xs: 1.5, sm: 3 } }}>{children}</Container>
  </Box>
);

function LoginView({ colors, onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await call("login", { email, password });
      if (r.code === 200) onLoggedIn({ token: r.token, name: r.member.name, mustChange: !!r.must_change_password });
      else setError(r.message || "Could not log in");
    } catch { setError("Cannot reach the server. Check your internet and try again."); }
    setBusy(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2,
      background: `radial-gradient(circle at 15% 10%, ${colors.leaf} 0%, ${colors.evergreen} 55%, #0b1a14 100%)` }}>
      <Card sx={{ width: "100%", maxWidth: 410, borderRadius: 4, boxShadow: "0 24px 60px rgba(0,0,0,0.35)", overflow: "visible" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: -5 }}>
          <Box sx={{ bgcolor: "#fff", borderRadius: "50%", p: 1.2, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", display: "flex" }}>
            <Box component="img" src={`${process.env.PUBLIC_URL || ""}/images/rectangle-9@2x.png`} alt="" sx={{ height: 56, width: 56, objectFit: "contain" }} />
          </Box>
        </Box>
        <CardContent component="form" onSubmit={submit} sx={{ p: 3.5, pt: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, textAlign: "center" }}>Welcome back</Typography>
          <Typography sx={{ color: "text.secondary", mb: 2.5, fontSize: 13.5, textAlign: "center" }}>
            See your membership, payments and invoices.
          </Typography>
          {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert> : null}
          <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" sx={{ mb: 2 }} required />
          <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" sx={{ mb: 1 }} required />
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2.5 }}>First time? Your password is your mobile number. You will choose a new one right after.</Typography>
          <Button type="submit" fullWidth variant="contained" disabled={busy}
            sx={{ background: `linear-gradient(135deg, ${colors.leaf}, ${colors.evergreen})`, py: 1.4, borderRadius: 3, fontWeight: 700, textTransform: "none", fontSize: 16 }}>
            {busy ? "Please wait…" : "Log in"}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

function PasswordForm({ session, forced, onDone, onExpired, colors }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== again) { setMsg({ type: "error", text: "The two new passwords do not match" }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await call("change-password", { current_password: current, new_password: next, new_password_confirmation: again }, session, onExpired);
      if (r.code === 200) { setCurrent(""); setNext(""); setAgain(""); setMsg({ type: "success", text: "Password changed" }); onDone && onDone(); }
      else setMsg({ type: "error", text: r.message || "Could not change the password" });
    } catch { setMsg({ type: "error", text: "Cannot reach the server" }); }
    setBusy(false);
  };

  return (
    <Box component="form" onSubmit={submit}>
      {forced ? (
        <Alert severity="info" sx={{ mb: 2 }}>For your safety, choose your own password now. Your mobile number can no longer be used to log in after this.</Alert>
      ) : null}
      {msg ? <Alert severity={msg.type} sx={{ mb: 2 }}>{msg.text}</Alert> : null}
      <TextField fullWidth label={forced ? "Current password (your mobile number)" : "Current password"} type="password" value={current} onChange={(e) => setCurrent(e.target.value)} sx={{ mb: 2 }} required />
      <TextField fullWidth label="New password (at least 6 characters)" type="password" value={next} onChange={(e) => setNext(e.target.value)} sx={{ mb: 2 }} required />
      <TextField fullWidth label="Repeat new password" type="password" value={again} onChange={(e) => setAgain(e.target.value)} sx={{ mb: 2 }} required />
      <Button type="submit" variant="contained" disabled={busy} sx={{ bgcolor: colors.leaf, "&:hover": { bgcolor: colors.evergreen } }}>Save password</Button>
    </Box>
  );
}

const Tile = ({ icon, label, value, color, bg }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 4, height: "100%", display: "flex", gap: 1.5, alignItems: "center", border: "1px solid #e8e8e0" }}>
    <Box sx={{ width: 44, height: 44, borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: bg || "#eef3ec", color: color || "#1f7a54", flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</Typography>
      <Typography noWrap sx={{ fontSize: 18, fontWeight: 800, color: color || "inherit" }}>{value}</Typography>
    </Box>
  </Paper>
);

const Stat = ({ label, value, color }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 4, height: "100%", border: "1px solid #e8e8e0" }}>
    <Typography sx={{ fontSize: 11.5, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 800, color: color || "inherit" }}>{value}</Typography>
  </Paper>
);

function Hero({ me, colors }) {
  return (
    <Box sx={{ borderRadius: 5, p: { xs: 2.5, sm: 3 }, color: "#fff", mb: 2, position: "relative", overflow: "hidden",
      background: `linear-gradient(135deg, ${colors.evergreen} 0%, ${colors.leaf} 100%)`, boxShadow: "0 14px 34px rgba(0,0,0,0.22)" }}>
      <Box sx={{ position: "absolute", right: -40, top: -40, width: 160, height: 160, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ position: "absolute", right: 40, bottom: -60, width: 120, height: 120, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)" }} />
      <Box sx={{ display: "flex", gap: 2.5, alignItems: "center", position: "relative" }}>
        <Avatar src={me.image || undefined} sx={{ width: 88, height: 88, bgcolor: colors.gold, color: colors.evergreen, fontSize: 34, fontWeight: 800, border: "3px solid rgba(255,255,255,0.85)" }}>
          {String(me.name || "?").trim().charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>Hello,</Typography>
          <Typography noWrap sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 800, lineHeight: 1.15 }}>{me.name}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}>
            <Chip size="small" label={`ID ${me.membership_number}`} sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600 }} />
            {me.package_name ? <Chip size="small" icon={<FitnessCenterIcon sx={{ color: "#fff !important" }} />} label={me.package_name} sx={{ bgcolor: colors.gold, color: colors.evergreen, fontWeight: 700 }} /> : null}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

// A smaller plan card (personal training / yearly membership) with its own dates and days left.
function ExtraPlan({ title, icon, plan, lines }) {
  if (!plan) return null; // nothing to show when the member has no such plan
  const dl = plan ? plan.days_left : null;
  const tone = dl === null ? "#64748b" : dl < 0 ? "#C0392B" : dl <= 7 ? "#d97706" : "#16803c";
  const tint = dl === null ? "#eef1f4" : dl < 0 ? "#fdecea" : dl <= 7 ? "#fef3c7" : "#e6f4ea";
  return (
    <Paper elevation={0} sx={{ p: 2.25, borderRadius: 4, height: "100%", border: "1px solid #e8e8e0" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "12px", bgcolor: tint, color: tone, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, flex: 1 }}>{title}</Typography>
        {plan ? <Chip size="small" sx={{ bgcolor: tint, color: tone, fontWeight: 700 }}
          label={dl === null ? "-" : dl < 0 ? `Expired ${Math.abs(dl)}d ago` : dl === 0 ? "Ends today" : `${dl} day${dl === 1 ? "" : "s"} left`} /> : null}
      </Box>
      {(
        <>
          {lines.filter(Boolean).map((l, i) => <Typography key={i} sx={{ fontSize: i === 0 ? 16 : 13.5, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? "inherit" : "text.secondary" }}>{l}</Typography>)}
          <Typography sx={{ color: "text.secondary", fontSize: 13.5, mt: 0.5 }}>{fmtDate(plan.start_date)}  →  {fmtDate(plan.end_date)}</Typography>
          {plan.due > 0 ? <Chip size="small" label={`Due ${money(plan.due)}`} sx={{ mt: 1, bgcolor: "#fdecea", color: "#C0392B", fontWeight: 700 }} /> : null}
        </>
      )}
    </Paper>
  );
}

function Home({ me, colors }) {
  if (!me) return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box>;
  const dl = me.days_left;
  const tone = dl === null ? "#64748b" : dl < 0 ? "#C0392B" : dl <= 7 ? "#d97706" : "#16803c";
  const tint = dl === null ? "#eef1f4" : dl < 0 ? "#fdecea" : dl <= 7 ? "#fef3c7" : "#e6f4ea";
  const total = me.start_date && me.end_date ? Math.max(1, Math.round((new Date(me.end_date) - new Date(me.start_date)) / 86400000)) : null;
  const pct = dl === null || !total ? 0 : Math.max(0, Math.min(100, (Math.max(dl, 0) / total) * 100));
  const label = dl === null ? "No active plan" : dl < 0 ? `Expired ${Math.abs(dl)} day${Math.abs(dl) === 1 ? "" : "s"} ago` : dl === 0 ? "Ends today" : `${dl === 1 ? "day" : "days"} left`;
  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 5, border: "1px solid #e8e8e0", display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <Ring variant="determinate" value={100} size={130} thickness={4} sx={{ color: tint, position: "absolute" }} />
          <Ring variant="determinate" value={pct} size={130} thickness={4} sx={{ color: tone, "& .MuiCircularProgress-circle": { strokeLinecap: "round" } }} />
          <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ fontSize: dl !== null && dl < 0 ? 26 : 34, fontWeight: 900, color: tone, lineHeight: 1 }}>{dl === null ? "-" : Math.abs(dl)}</Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{dl !== null && dl < 0 ? "days over" : "days left"}</Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
          <Chip label={label} sx={{ bgcolor: tint, color: tone, fontWeight: 700, mb: 1 }} />
          <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{me.package_name || "No active package"}</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            {me.start_date ? `${fmtDate(me.start_date)}  →  ${fmtDate(me.end_date)}` : "Ask the gym to renew your plan"}
          </Typography>
        </Box>
      </Paper>
      <Grid container spacing={2}>
        {me.pt ? <Grid size={{ xs: 12, sm: 6 }}>
          <ExtraPlan title="Personal training" icon={<FitnessCenterIcon />} plan={me.pt}
            lines={[me.pt.package_name, me.pt.trainer ? `Trainer: ${me.pt.trainer}` : null, me.pt.slot ? `Slot: ${me.pt.slot}` : null]} />
        </Grid> : null}
        {me.yearly ? <Grid size={{ xs: 12, sm: 6 }}>
          <ExtraPlan title="Yearly membership" icon={<EventAvailableIcon />} plan={me.yearly}
            lines={[`Fee ${money(me.yearly.amount)}`]} />
        </Grid> : null}
      </Grid>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}><Tile icon={<EventAvailableIcon />} label="Started" value={fmtDate(me.start_date)} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}><Tile icon={<EventBusyIcon />} label="Ends" value={fmtDate(me.end_date)} color={tone} bg={tint} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}><Tile icon={<AccountBalanceWalletIcon />} label="Payment due" value={money(me.due_amount)} color={me.due_amount > 0 ? "#C0392B" : "#16803c"} bg={me.due_amount > 0 ? "#fdecea" : "#e6f4ea"} /></Grid>
      </Grid>
    </Stack>
  );
}

const Empty = ({ text }) => <Typography sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>{text}</Typography>;

function Payments({ rows }) {
  if (!rows) return <CircularProgress size={24} />;
  if (!rows.length) return <Empty text="No payments yet" />;
  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflowX: "auto", border: "1px solid #e8e8e0" }}>
      <Table size="small">
        <TableHead><TableRow><TableCell>Date</TableCell><TableCell>For</TableCell><TableCell align="right">Paid</TableCell><TableCell align="right">Due</TableCell><TableCell>Mode</TableCell></TableRow></TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{fmtDate(r.date)}</TableCell>
              <TableCell>{r.name}<Typography component="div" sx={{ fontSize: 11, color: "text.secondary" }}>{r.type}</Typography></TableCell>
              <TableCell align="right">{money(r.paid)}</TableCell>
              <TableCell align="right" sx={{ color: r.due > 0 ? "#C0392B" : "inherit" }}>{money(r.due)}</TableCell>
              <TableCell sx={{ textTransform: "capitalize" }}>{r.mode || "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Every invoice of the member (membership, personal training, yearly, steam bath), narrowed by year, month and package.
function Invoices({ rows, onOpen }) {
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [pkg, setPkg] = useState("");
  if (!rows) return <CircularProgress size={24} />;
  if (!rows.length) return <Empty text="No invoices yet" />;

  const pkgKey = (r) => `${r.type || "Invoice"} · ${r.name}`;
  const years = [...new Set(rows.map((r) => (r.date || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const pkgs = [...new Set(rows.map(pkgKey))].sort();
  const shown = rows.filter((r) => {
    const d = r.date || "";
    return (!year || d.slice(0, 4) === year) && (!month || d.slice(5, 7) === month) && (!pkg || pkgKey(r) === pkg);
  });
  const filtered = year || month || pkg;
  const total = shown.reduce((sum, r) => sum + Number(r.paid || 0), 0);

  return (
    <Stack spacing={1.5}>
      <Paper elevation={0} sx={{ p: 1.5, borderRadius: 4, border: "1px solid #e8e8e0", display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField select size="small" label="Year" value={year} onChange={(e) => setYear(e.target.value)} sx={{ minWidth: 110, flex: 1 }}>
          <MenuItem value="">All years</MenuItem>
          {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Month" value={month} onChange={(e) => setMonth(e.target.value)} sx={{ minWidth: 130, flex: 1 }}>
          <MenuItem value="">All months</MenuItem>
          {MONTHS.map((m, i) => <MenuItem key={m} value={String(i + 1).padStart(2, "0")}>{m}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Package" value={pkg} onChange={(e) => setPkg(e.target.value)} sx={{ minWidth: 190, flex: 2 }}>
          <MenuItem value="">All packages</MenuItem>
          {pkgs.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
        </TextField>
        {filtered ? <Button size="small" onClick={() => { setYear(""); setMonth(""); setPkg(""); }} sx={{ textTransform: "none" }}>Clear</Button> : null}
      </Paper>
      <Typography sx={{ color: "text.secondary", fontSize: 13, px: 0.5 }}>
        {shown.length} invoice{shown.length === 1 ? "" : "s"}{filtered ? " found" : ""} · paid {money(total)}
      </Typography>
      {!shown.length ? <Empty text="No invoices match these filters" /> : shown.map((r) => (
        <Paper key={r.id} elevation={0} sx={{ p: 2, borderRadius: 4, display: "flex", alignItems: "center", gap: 2, border: "1px solid #e8e8e0", "&:hover": { boxShadow: "0 6px 18px rgba(0,0,0,0.07)" } }}>
          <Box sx={{ width: 42, height: 42, borderRadius: "13px", bgcolor: "#eef3ec", color: "#1f7a54", display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center" }}><ReceiptIcon /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>{r.name}</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{r.type} · Invoice #{r.id} · {fmtDate(r.date)}</Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontWeight: 700 }}>{money(r.paid)}</Typography>
            {r.due > 0 ? <Chip size="small" label={`Due ${money(r.due)}`} sx={{ bgcolor: "#fdecea", color: "#C0392B" }} /> : null}
          </Box>
          <Button size="small" variant="outlined" onClick={() => onOpen(r.id)} sx={{ borderRadius: 99, textTransform: "none" }}>View</Button>
        </Paper>
      ))}
    </Stack>
  );
}

// Same layout as the invoice staff print from the admin screens.
function InvoiceView({ id, session, onClose, onExpired }) {
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const sheetRef = React.useRef();
  const handlePrint = useReactToPrint({ content: () => sheetRef.current, documentTitle: `Invoice_${inv?.membership_number || id}` });
  useEffect(() => {
    let live = true;
    call("invoice", { invoice_number: id }, session, onExpired)
      .then((r) => { if (!live) return; if (r.code === 200 && r.data) setInv(r.data); else setErr(r.message || "Invoice not found"); })
      .catch(() => live && setErr("Cannot reach the server"));
    return () => { live = false; };
  }, [id]);
  const blue = "#0077b6";
  const th = { fontWeight: "bold", backgroundColor: blue, color: "#fff" };
  return (
    <Box sx={{ backgroundColor: "#e7f3fe", borderRadius: 2, p: { xs: 1.5, sm: 2.5 }, boxShadow: "0 4px 15px rgba(0,0,0,0.2)" }}>
      <Button className="no-print" variant="contained" color="secondary" onClick={onClose} startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>Go Back</Button>
      {err ? <Alert severity="error">{err}</Alert> : !inv ? <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box> : (
        <Box ref={sheetRef} sx={{ backgroundColor: "#fff", borderRadius: 3, p: { xs: 2, sm: 4 }, fontFamily: '"Roboto", sans-serif', boxShadow: "0 4px 10px rgba(0,0,0,0.1)" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 1 }}>
            <Box component="img" src="/images/rectangle-9@2x.png" alt="Gym Logo" sx={{ width: 160, maxWidth: "40%" }} />
            <Box sx={{ textAlign: "right", flex: 1, minWidth: 200 }}>
              <Typography variant="h5" sx={{ color: blue }}>Maa Tara Tarini Multi gym</Typography>
              <Typography variant="subtitle2" sx={{ color: "#333" }}>Address: 167, E Canal Rd, Dayanandnagar, Bomikhal, Rasulgarh, Bhubaneswar, Odisha 751010</Typography>
              <Typography variant="body2" sx={{ color: "#777" }}>Email: sagarparida29@gmail.com | Phone: +91 9937542268</Typography>
            </Box>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center", color: blue }} gutterBottom>Invoice</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
            <Typography><strong>Invoice No:</strong> {inv.invoice_number ? `INV${inv.invoice_number}` : "N/A"}</Typography>
            <Typography><strong>Date:</strong> {inv.date || "N/A"}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
            <Typography><strong>Member Name:</strong> {inv.name || "N/A"}</Typography>
            <Typography><strong>Phone Number:</strong> {inv.phone || "N/A"}</Typography>
          </Box>
          <Typography sx={{ mb: 3 }}><strong>Membership ID:</strong> {inv.membership_number || "N/A"}</Typography>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: blue }}>Package Details</Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ mt: 1, backgroundColor: "#f9f9f9" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={th}>Package</TableCell><TableCell sx={th}>Package Value</TableCell><TableCell sx={th}>Discount</TableCell>
                  <TableCell sx={th}>Payable Amount</TableCell><TableCell sx={th}>Paid Amount</TableCell><TableCell sx={th}>Due Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{inv.package_name || "N/A"}</TableCell><TableCell>{inv.package_value ?? "N/A"}</TableCell><TableCell>{inv.discount ?? 0}</TableCell>
                  <TableCell>{inv.payable_amount ?? "N/A"}</TableCell><TableCell>{inv.paying_amount ?? "N/A"}</TableCell><TableCell>{inv.due_amount ?? "N/A"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
          <Box sx={{ mt: 4, textAlign: "center", p: 2.5, backgroundColor: "#e0f7fa", borderRadius: 2 }}>
            <p style={{ color: "#555", margin: 0, fontSize: "1rem", lineHeight: 1.5 }}>Maa Tara Tarini Multi gym is a cutting-edge functional fitness system that can help everyday men.</p>
          </Box>
        </Box>
      )}
      <Box className="no-print" sx={{ mt: 3, textAlign: "center" }}>
        <Button variant="contained" color="primary" onClick={handlePrint} disabled={!inv} sx={{ py: 1.2, px: 2.5, fontSize: 16 }}>Print / Download Invoice</Button>
      </Box>
    </Box>
  );
}

function Attendance({ data }) {
  if (!data) return <CircularProgress size={24} />;
  if (!data.data.length) return <Empty text="No visits in the last 60 days" />;
  return (
    <Stack spacing={1.5}>
      <Stat label="Visits this month" value={data.this_month} />
      <Paper elevation={0} sx={{ borderRadius: 4, overflowX: "auto", border: "1px solid #e8e8e0" }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Date</TableCell><TableCell>In</TableCell><TableCell>Out</TableCell></TableRow></TableHead>
          <TableBody>
            {data.data.map((r, i) => (
              <TableRow key={i}><TableCell>{fmtDate(r.date)}</TableCell><TableCell>{r.check_in || "-"}</TableCell><TableCell>{r.check_out || "-"}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

// The member can only look at their details here; changes are made by the gym.
function Profile({ me }) {
  if (!me) return <CircularProgress size={24} />;
  const age = formatAge(me.dob);
  const rows = [
    ["Membership ID", me.membership_number], ["Name", me.name], ["Email", me.email], ["Mobile", me.phone],
    ["Alternate mobile", me.alternate_phone], ["Date of birth", me.dob ? `${fmtDate(me.dob)}${age ? ` · ${age}` : ""}` : ""],
    ["Gender", me.sex], ["Address", me.address], ["Height", me.height], ["Weight", me.weight],
    ["ID type", me.identification_type], ["ID number", me.identification_id],
  ].filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "");
  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: 3 }}>To change any of these details, please ask the gym.</Alert>
      <Paper elevation={0} sx={{ borderRadius: 4, border: "1px solid #e8e8e0", overflow: "hidden" }}>
        {rows.map(([k, v], i) => (
          <Box key={k} sx={{ display: "flex", justifyContent: "space-between", gap: 2, px: 2.5, py: 1.5, borderTop: i ? "1px solid #f0f0ea" : "none" }}>
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>{k}</Typography>
            <Typography sx={{ fontWeight: 600, textAlign: "right", textTransform: k === "Gender" ? "capitalize" : "none", wordBreak: "break-word" }}>{v}</Typography>
          </Box>
        ))}
      </Paper>
    </Stack>
  );
}

export default function MemberPortal() {
  const { colors } = useAppTheme();
  const [session, setSession] = useState(readSession);
  const [tab, setTab] = useState("home");
  const [me, setMe] = useState(null);
  const [payments, setPayments] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [openInvoice, setOpenInvoice] = useState(null);
  const [sessionNote, setSessionNote] = useState("");

  const logout = useCallback(async (note) => {
    const s = readSession();
    if (s && !note) { try { await call("logout", {}, s); } catch { /* ignore */ } }
    writeSession(null); setSession(null); setMe(null); setPayments(null); setInvoices(null); setAttendance(null); setTab("home");
    setSessionNote(typeof note === "string" ? note : "");
  }, []);
  const expired = useCallback(() => logout("Your session ended. Please log in again."), [logout]);

  const loadMe = useCallback(() => {
    if (!session || session.mustChange) return;
    call("me", {}, session, expired).then((r) => { if (r.code === 200) setMe(r.data); }).catch(() => {});
  }, [session, expired]);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => {
    if (!session || session.mustChange) return;
    if (tab === "payments" && !payments) call("payments", {}, session, expired).then((r) => setPayments(r.data || [])).catch(() => setPayments([]));
    if (tab === "invoices" && !invoices) call("invoices", {}, session, expired).then((r) => setInvoices(r.data || [])).catch(() => setInvoices([]));
    if (tab === "attendance" && !attendance) call("attendance", {}, session, expired).then((r) => setAttendance({ data: r.data || [], this_month: r.this_month || 0 })).catch(() => setAttendance({ data: [], this_month: 0 }));
  }, [tab, session]);

  if (!session) {
    return (
      <>
        {sessionNote ? <Alert severity="info" sx={{ m: 2 }}>{sessionNote}</Alert> : null}
        <LoginView colors={colors} onLoggedIn={(s) => { writeSession(s); setSession(s); setSessionNote(""); }} />
      </>
    );
  }

  if (session.mustChange) {
    return (
      <Shell colors={colors} name={session.name} onLogout={() => logout()}>
        <Card elevation={0} sx={{ maxWidth: 460, mx: "auto", borderRadius: 4, border: "1px solid #e8e8e0", boxShadow: "0 14px 34px rgba(0,0,0,0.08)" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Choose your password</Typography>
            <PasswordForm session={session} forced colors={colors} onExpired={expired}
              onDone={() => { const s = { ...session, mustChange: false }; writeSession(s); setSession(s); }} />
          </CardContent>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell colors={colors} name={me?.name || session.name} onLogout={() => logout()}>
      <style>{`@media print { .no-print { display: none !important; } body * { visibility: hidden; } .print-area, .print-area * { visibility: visible; } }`}</style>
      {openInvoice ? <InvoiceView id={openInvoice} session={session} onClose={() => setOpenInvoice(null)} onExpired={expired} /> : <>
      {me ? <Hero me={me} colors={colors} /> : null}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile className="no-print"
        TabIndicatorProps={{ style: { display: "none" } }}
        sx={{ mb: 2, minHeight: 0, "& .MuiTab-root": { minHeight: 0, minWidth: 0, textTransform: "none", fontWeight: 600, borderRadius: 99, px: 2, py: 1, mr: 1, bgcolor: "#fff", border: "1px solid #e3e3da", color: "text.secondary" },
          "& .Mui-selected": { color: "#fff !important", bgcolor: colors.leaf, borderColor: colors.leaf } }}>
        <Tab value="home" icon={<HomeRoundedIcon fontSize="small" />} iconPosition="start" label="My plan" />
        <Tab value="payments" icon={<PaymentsIcon fontSize="small" />} iconPosition="start" label="Payments" />
        <Tab value="invoices" icon={<ReceiptLongIcon fontSize="small" />} iconPosition="start" label="Invoices" />
        <Tab value="attendance" icon={<CalendarMonthIcon fontSize="small" />} iconPosition="start" label="Attendance" />
        <Tab value="profile" icon={<PersonIcon fontSize="small" />} iconPosition="start" label="My details" />
        <Tab value="password" icon={<LockIcon fontSize="small" />} iconPosition="start" label="Password" />
      </Tabs>
      {tab === "home" ? <Home me={me} colors={colors} /> : null}
      {tab === "payments" ? <Payments rows={payments} /> : null}
      {tab === "invoices" ? <Invoices rows={invoices} onOpen={setOpenInvoice} /> : null}
      {tab === "attendance" ? <Attendance data={attendance} /> : null}
      {tab === "profile" ? <Profile me={me} /> : null}
      {tab === "password" ? (
        <Card elevation={0} sx={{ maxWidth: 460, borderRadius: 4, border: "1px solid #e8e8e0" }}><CardContent sx={{ p: 3 }}><PasswordForm session={session} colors={colors} onExpired={expired} /></CardContent></Card>
      ) : null}
      </>}

    </Shell>
  );
}
